import React, { useEffect, useState } from "react";
import { Download, FileText, X } from "lucide-react";
import toast from "react-hot-toast";
import {
  DsButton,
  DsCard,
  DsIcon,
} from "../../design-system";

/** Theme-tinted centered loader (Payroll Dashboard pattern). */
export function DashboardLoadingOverlay({ show, label = "Loading analytics…" }) {
  if (!show) return null;
  return (
    <div
      className="absolute inset-0 z-40 flex items-center justify-center"
      style={{ background: "hsl(var(--ds-primary) / 0.12)" }}
      aria-busy="true"
      aria-live="polite"
    >
      <div className="flex flex-col items-center gap-3 rounded-2xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface)/0.92)] px-8 py-6 shadow-lg backdrop-blur-sm">
        <svg className="h-14 w-14 -rotate-90" viewBox="0 0 56 56" aria-hidden>
          <circle cx="28" cy="28" r="22" fill="none" stroke="hsl(var(--ds-muted))" strokeWidth="5" />
          <circle
            cx="28"
            cy="28"
            r="22"
            fill="none"
            stroke="hsl(var(--ds-primary))"
            strokeWidth="5"
            strokeLinecap="round"
            strokeDasharray="100 40"
            className="animate-spin origin-center"
            style={{ transformOrigin: "28px 28px" }}
          />
        </svg>
        <div className="text-sm font-semibold text-[hsl(var(--ds-fg))]">{label}</div>
      </div>
    </div>
  );
}

export function DashboardMetricCard({
  label,
  value,
  sub,
  icon: Icon,
  tone = "default",
  spark = null,
  onClick = null,
  clickHint = true,
}) {
  const valueTone =
    tone === "up"
      ? "text-[hsl(var(--ds-success))]"
      : tone === "down"
        ? "text-[hsl(var(--ds-danger))]"
        : "text-[hsl(var(--ds-fg))]";

  const inner = (
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0 flex-1">
        <div className="ds-small uppercase tracking-wide text-[hsl(var(--ds-muted-fg))] text-[10px] leading-tight">
          {label}
        </div>
        <div className={`mt-0.5 text-base font-bold leading-tight tabular-nums ${valueTone}`}>
          {value}
        </div>
        {sub ? (
          <div className="ds-caption mt-0.5 text-[10px] leading-snug text-[hsl(var(--ds-muted-fg))]">
            {sub}
          </div>
        ) : null}
        {spark ? <div className="mt-1">{spark}</div> : null}
        {onClick && clickHint ? (
          <div className="mt-1 text-[9px] text-[hsl(var(--ds-primary))]">Click for details</div>
        ) : null}
      </div>
      {Icon ? (
        <DsIcon icon={Icon} size="sm" className="text-[hsl(var(--ds-secondary))] opacity-70" />
      ) : null}
    </div>
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className="w-full rounded-[var(--ds-radius-md)] text-left outline-none transition hover:ring-2 hover:ring-[hsl(var(--ds-ring))] focus-visible:ring-2 focus-visible:ring-[hsl(var(--ds-ring))]"
      >
        <DsCard padding="sm" className="!p-2.5 h-full">
          {inner}
        </DsCard>
      </button>
    );
  }

  return (
    <DsCard padding="sm" className="!p-2.5">
      {inner}
    </DsCard>
  );
}

export function DashboardCompareModal({
  open,
  onClose,
  availableMonths = [],
  selected = [],
  aspects = [],
  selectedAspects = [],
  onApply,
  title = "Compare periods & aspects",
}) {
  const [picked, setPicked] = useState(selected);
  const [aspectIds, setAspectIds] = useState(selectedAspects);

  useEffect(() => {
    if (!open) return;
    setPicked(selected);
    setAspectIds(selectedAspects?.length ? selectedAspects : aspects.map((a) => a.id));
  }, [open, selected, selectedAspects, aspects]);

  if (!open) return null;

  const toggle = (ym) => {
    setPicked((prev) => {
      if (prev.includes(ym)) return prev.filter((x) => x !== ym);
      if (prev.length >= 5) {
        toast.error("Select up to 5 periods");
        return prev;
      }
      return [...prev, ym].sort();
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-xl rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] shadow-lg">
        <div className="flex items-center justify-between border-b border-[hsl(var(--ds-border))] px-4 py-3">
          <div>
            <div className="ds-h4">{title}</div>
            <div className="ds-caption text-[hsl(var(--ds-muted-fg))]">
              Choose 2–5 months and which aspects to compare.
            </div>
          </div>
          <DsButton type="button" variant="ghost" size="sm" onClick={onClose}>
            <DsIcon icon={X} size="sm" />
          </DsButton>
        </div>
        <div className="max-h-[70vh] space-y-4 overflow-y-auto px-4 py-3">
          <div>
            <div className="mb-2 text-xs font-semibold uppercase text-[hsl(var(--ds-muted-fg))]">
              Months
            </div>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {availableMonths.map((m) => {
                const ym = m.payMonth || m;
                const on = picked.includes(ym);
                return (
                  <button
                    key={ym}
                    type="button"
                    onClick={() => toggle(ym)}
                    className={`rounded-md border px-2 py-1.5 text-left text-xs ${
                      on
                        ? "border-[hsl(var(--ds-primary))] bg-[hsl(var(--ds-primary)/0.12)]"
                        : "border-[hsl(var(--ds-border))]"
                    }`}
                  >
                    {ym}
                  </button>
                );
              })}
            </div>
          </div>
          {aspects.length ? (
            <div>
              <div className="mb-2 text-xs font-semibold uppercase text-[hsl(var(--ds-muted-fg))]">
                Aspects
              </div>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                {aspects.map((a) => {
                  const on = aspectIds.includes(a.id);
                  return (
                    <label
                      key={a.id}
                      className={`flex cursor-pointer items-center gap-2 rounded-md border px-2 py-1.5 text-xs ${
                        on
                          ? "border-[hsl(var(--ds-primary))] bg-[hsl(var(--ds-primary)/0.08)]"
                          : "border-[hsl(var(--ds-border))]"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={on}
                        onChange={() =>
                          setAspectIds((prev) =>
                            prev.includes(a.id)
                              ? prev.filter((x) => x !== a.id)
                              : [...prev, a.id]
                          )
                        }
                      />
                      {a.label}
                    </label>
                  );
                })}
              </div>
            </div>
          ) : null}
        </div>
        <div className="flex justify-end gap-2 border-t border-[hsl(var(--ds-border))] px-4 py-3">
          <DsButton type="button" variant="outline" size="sm" onClick={onClose}>
            Cancel
          </DsButton>
          <DsButton
            type="button"
            size="sm"
            disabled={picked.length < 2}
            onClick={() => {
              onApply({ months: picked, aspects: aspectIds });
              onClose();
            }}
          >
            Apply comparison
          </DsButton>
        </div>
      </div>
    </div>
  );
}

export function DashboardCardDetailModal({
  open,
  detail,
  onClose,
  canExport,
  onExportExcel,
  onExportPdf,
}) {
  const [exporting, setExporting] = useState(false);
  if (!open || !detail) return null;

  const run = async (fn, kind) => {
    if (!canExport) {
      toast.error("No export permission");
      return;
    }
    setExporting(true);
    try {
      const name = await fn(detail);
      toast.success(name ? `Exported ${name}` : `${kind} exported`);
    } catch (e) {
      toast.error(e.message || `${kind} export failed`);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="flex max-h-[85vh] w-full max-w-lg flex-col rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] shadow-lg">
        <div className="flex items-start justify-between gap-2 border-b border-[hsl(var(--ds-border))] px-4 py-3">
          <div>
            <div className="ds-h4">{detail.title}</div>
            <div className="ds-caption text-[hsl(var(--ds-muted-fg))]">
              {detail.periodLabel || ""}
            </div>
          </div>
          <DsButton type="button" variant="ghost" size="sm" onClick={onClose}>
            <DsIcon icon={X} size="sm" />
          </DsButton>
        </div>
        <div className="flex-1 overflow-y-auto px-4 py-3">
          <table className="ds-table w-full text-xs">
            <tbody>
              {(detail.summary || []).map(([k, v]) => (
                <tr key={k} className="border-b border-[hsl(var(--ds-border))]">
                  <td className="py-1.5 pr-3 text-[hsl(var(--ds-muted-fg))]">{k}</td>
                  <td className="py-1.5 text-right font-semibold tabular-nums">
                    {v == null ? "—" : String(v)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {detail.note ? (
            <p className="mt-3 text-[10px] text-[hsl(var(--ds-muted-fg))]">{detail.note}</p>
          ) : null}
        </div>
        <div className="flex flex-wrap justify-end gap-2 border-t border-[hsl(var(--ds-border))] px-4 py-3">
          {canExport && onExportExcel ? (
            <DsButton
              type="button"
              variant="outline"
              size="sm"
              disabled={exporting}
              onClick={() => run(onExportExcel, "Excel")}
            >
              <DsIcon icon={Download} size="sm" />
              Excel
            </DsButton>
          ) : null}
          {canExport && onExportPdf ? (
            <DsButton
              type="button"
              variant="outline"
              size="sm"
              disabled={exporting}
              onClick={() => run(onExportPdf, "PDF")}
            >
              <DsIcon icon={FileText} size="sm" />
              PDF
            </DsButton>
          ) : null}
          <DsButton type="button" size="sm" onClick={onClose}>
            Close
          </DsButton>
        </div>
      </div>
    </div>
  );
}

/** Last N calendar months as YYYY-MM for compare pickers. */
export function recentPayMonths(count = 18) {
  const out = [];
  const d = new Date();
  for (let i = 0; i < count; i += 1) {
    const y = d.getFullYear();
    const m = d.getMonth() + 1;
    out.push(`${y}-${String(m).padStart(2, "0")}`);
    d.setMonth(d.getMonth() - 1);
  }
  return out;
}
